/**
 * Servizio puro per il **rewind** del PG ad uno stato di livello passato
 * (FI-46 — LevelsRail navigabile). Inverte gli effetti dei `LevelUpRecord`
 * applicati dopo il livello target, ricostruendo deterministicamente lo
 * stato del Character a fine `L{targetLevel - 1}`.
 *
 * **Scope FI-46 (PR-A backend)**: questa primitive abilita la PR-B (UI)
 * che permetterà all'utente di cliccare un livello passato nella
 * `<LevelsRail>` per riproporre il `LevelUpFlowPage` di quel livello,
 * pre-popolato dalle scelte storiche del corrispondente `LevelUpRecord`.
 *
 * **Convenzione `targetLevel`**: livello al quale si vuole far tornare il
 * PG (output = stato a *fine* `L{targetLevel}`). `level === targetLevel`,
 * `levelUpHistory.length === targetLevel - 1`.
 *
 * Per l'UX "voglio rifare L3" il chiamante (PR-B) usa
 * `rewindToLevel(c, 2)` → PG a fine L2, pronto a invocare `applyLevelUp`
 * per L3. Per "L1 baseline" (FI-46bis builder): `rewindToLevel(c, 1)`.
 *
 * **Limitazione nota**: il `LevelUpRecord.movesReplaced` memorizza solo
 * gli ID delle mosse soppiantate. Dopo il rewind, le mosse reintegrate
 * tornano con `selectedChoices: []` — eventuale setup originale è perso.
 * Migration `movesReplaced: MoveRef[]` rinviata a FI futuro se necessario.
 */

import { dwrClassesById } from '../data/classes';
import { CharacterSchema, type Character, type MoveRef, type SpellRef } from '../schemas/character';
import type { LevelUpRecord } from '../schemas/levelUpRecord';
import { computeMaxHp } from './characterCreation';
import { getFollowersFromDraft } from './followerCreation';

/**
 * Riporta il PG allo stato di fine `L{targetLevel}`.
 *
 * Pure function, idempotente: `rewindToLevel(rewindToLevel(c, N), N)`
 * produce lo stesso risultato di `rewindToLevel(c, N)` (a meno del campo
 * `updatedAt`, ri-aggiornato ad ogni invocazione).
 *
 * @param character PG corrente (qualsiasi livello ≥ 1)
 * @param targetLevel livello target (1..character.level)
 * @returns Character con `level = targetLevel`, `levelUpHistory` troncato
 *          a `targetLevel - 1` entry, stats/moves/spells/secondaryArchetypeId/pastTen
 *          "annullati" per i livelli > targetLevel, hp ricalcolato,
 *          followers re-derivati
 * @throws Error se `targetLevel < 1` o `targetLevel > character.level`
 */
export function rewindToLevel(character: Character, targetLevel: number): Character {
  if (targetLevel < 1) {
    throw new Error(`targetLevel deve essere ≥ 1, ricevuto ${targetLevel}.`);
  }
  if (targetLevel > character.level) {
    throw new Error(
      `targetLevel ${targetLevel} > level corrente ${character.level}: rewind nel futuro non supportato.`,
    );
  }
  if (targetLevel === character.level) {
    // No-op: il PG è già al livello target. Ritorniamo invariato
    // (incluso `updatedAt`) per preservare idempotenza esatta.
    return character;
  }

  // Record da invertire: tutti quelli con `levelReached > targetLevel`.
  // Es: PG L5 con history [r2,r3,r4,r5], targetLevel=3 → invertire [r4, r5]
  // (slice(targetLevel - 1) = slice(2) = [r4, r5]).
  const recordsToRevert = character.levelUpHistory.slice(targetLevel - 1);

  const reverted = recordsToRevert.reduce(revertRecord, {
    stats: { ...character.stats },
    moves: [...character.moves],
    spells: [...character.spells],
    secondaryArchetypeId: character.secondaryArchetypeId,
  });

  const newLevel = targetLevel;
  const newPastTen = newLevel <= 10 ? undefined : character.pastTen;

  // Ricalcolo HP: COS può essere diversa (stat increase invertito), e/o
  // secondaryArchetypeId può essere passato da X a undefined (IC invertita).
  const classDef = dwrClassesById[character.classId];
  if (!classDef) {
    throw new Error(`Class "${character.classId}" non trovata nel catalogo.`);
  }
  const newHpMax = computeMaxHp(
    classDef,
    reverted.stats.costituzione,
    character.archetypeId,
    reverted.secondaryArchetypeId,
  );
  const hpDelta = newHpMax - character.hp.max;
  const newHp = {
    max: newHpMax,
    current: Math.max(0, Math.min(newHpMax, character.hp.current + hpDelta)),
  };

  const newFollowers = getFollowersFromDraft(character.classId, reverted.moves, newLevel);

  const candidate: Character = {
    ...character,
    level: newLevel,
    stats: reverted.stats,
    hp: newHp,
    moves: reverted.moves,
    spells: reverted.spells,
    followers: newFollowers,
    levelUpHistory: character.levelUpHistory.slice(0, targetLevel - 1),
    // (length = targetLevel - 1, coerente con invariante history.length === level - 1)
    ...(reverted.secondaryArchetypeId !== undefined
      ? { secondaryArchetypeId: reverted.secondaryArchetypeId }
      : {}),
    ...(newPastTen !== undefined ? { pastTen: newPastTen } : {}),
    updatedAt: new Date().toISOString(),
  };

  // `exactOptionalPropertyTypes`: per "rimuovere" i campi opzionali quando
  // l'inversione li azzera, costruiamo l'oggetto senza le chiavi.
  if (reverted.secondaryArchetypeId === undefined && 'secondaryArchetypeId' in candidate) {
    delete (candidate as { secondaryArchetypeId?: string }).secondaryArchetypeId;
  }
  if (newPastTen === undefined && 'pastTen' in candidate) {
    delete (candidate as { pastTen?: Character['pastTen'] }).pastTen;
  }

  return CharacterSchema.parse(candidate);
}

// ---------------------------------------------------------------------------
// Inversione di un singolo record
// ---------------------------------------------------------------------------

interface RewindState {
  stats: Character['stats'];
  moves: MoveRef[];
  spells: SpellRef[];
  secondaryArchetypeId: string | undefined;
}

function revertRecord(state: RewindState, record: LevelUpRecord): RewindState {
  const nextStats = revertStat(state.stats, record);
  const movesAfterRemoval = revertMovesRemoval(state.moves, record);
  const movesAfterChoice = revertGrantedChoiceMove(movesAfterRemoval, record);
  const movesFinal = revertMovesReplaced(movesAfterChoice, record);
  const nextSpells = revertSpells(state.spells, record);
  const nextSecondaryArchetypeId =
    record.secondaryArchetypeAdded !== undefined ? undefined : state.secondaryArchetypeId;
  return {
    stats: nextStats,
    moves: movesFinal,
    spells: nextSpells,
    secondaryArchetypeId: nextSecondaryArchetypeId,
  };
}

function revertStat(stats: Character['stats'], record: LevelUpRecord): Character['stats'] {
  if (record.statIncreased === undefined) return stats;
  return { ...stats, [record.statIncreased]: stats[record.statIncreased] - 1 };
}

/**
 * Rimuove dalla lista `moves` gli ID introdotti dal record:
 *  - `moveAdded` (mossa principale)
 *  - `movesBundled` (mosse del bundle all-inclusive)
 *  - `grantedChoice.selectedOptionId` se kind ∈ {`from-group`, `from-choice-mapping`}
 *  - `multiclassPick.selectedMoveId`
 *
 * NB: `grantedChoice.kind === 'from-choice-move'` NON rimuove una mossa
 * (estende `selectedChoices` di una mossa esistente) — gestita in `revertGrantedChoiceMove`.
 */
function revertMovesRemoval(moves: readonly MoveRef[], record: LevelUpRecord): MoveRef[] {
  const idsToRemove = new Set<string>([record.moveAdded, ...record.movesBundled]);
  if (
    record.grantedChoice?.kind === 'from-group' ||
    record.grantedChoice?.kind === 'from-choice-mapping'
  ) {
    idsToRemove.add(record.grantedChoice.selectedOptionId);
  }
  if (record.multiclassPick !== undefined) {
    idsToRemove.add(record.multiclassPick.selectedMoveId);
  }
  return moves.filter((m) => !idsToRemove.has(m.id));
}

/**
 * Per granted `from-choice-move`: rimuove la `selectedOptionId` dal
 * `MoveRef` sorgente (la grant aveva esteso `selectedChoices` + l'eventuale
 * `selectedChoiceGroups.main`).
 */
function revertGrantedChoiceMove(moves: readonly MoveRef[], record: LevelUpRecord): MoveRef[] {
  if (record.grantedChoice?.kind !== 'from-choice-move') return [...moves];
  const { sourceMoveId, selectedOptionId } = record.grantedChoice;
  return moves.map((m) => {
    if (m.id !== sourceMoveId) return m;
    const restored: MoveRef = {
      ...m,
      selectedChoices: m.selectedChoices.filter((c) => c !== selectedOptionId),
    };
    if (m.selectedChoiceGroups?.main !== undefined) {
      restored.selectedChoiceGroups = {
        ...m.selectedChoiceGroups,
        main: m.selectedChoiceGroups.main.filter((c) => c !== selectedOptionId),
      };
    }
    return restored;
  });
}

/**
 * Re-integra le mosse `movesReplaced` (rimosse all'epoca del record).
 * **Limitazione nota**: solo ID, niente setup originale → reintegra come
 * `MoveRef` minimale `{ id, selectedChoices: [] }`. Vedi WIP doc FI-46.
 */
function revertMovesReplaced(moves: readonly MoveRef[], record: LevelUpRecord): MoveRef[] {
  if (record.movesReplaced.length === 0) return [...moves];
  const ownedIds = new Set(moves.map((m) => m.id));
  const toReintegrate: MoveRef[] = record.movesReplaced
    .filter((id) => !ownedIds.has(id))
    .map((id) => ({ id, selectedChoices: [] }));
  return [...moves, ...toReintegrate];
}

function revertSpells(spells: readonly SpellRef[], record: LevelUpRecord): SpellRef[] {
  if (record.spellsAdded.length === 0) return [...spells];
  const spellIdsToRemove = new Set(record.spellsAdded.map((s) => s.id));
  return spells.filter((s) => !spellIdsToRemove.has(s.id));
}
